import { isSessionTimingProjection, isActivityScheduleProjection } from './timing-projections.js';
/** A core activity endpoint returned a payload outside its documented shape. */
import { readVenueDetailResponse } from './venue-responses.js';
import { readSessionRecordResponse, isPersonRef, isVenue, isVenueRequest, isTypedDetails, isCurriculumLesson } from './session-responses.js';
import { isCurriculumStructure } from './curriculum-contracts.js';
import { isCurriculumPointer, isCurriculumProgress } from './typed-details.js';
import { isCertificationProjection } from './certification-contracts.js';
import { isRelationshipPoint } from './relationship-projections.js';
import {readOccurrenceReadContext} from './occurrence-read-contract.js';

export class ActivityResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'ActivityResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isId = value => typeof value === 'string' && value.length > 0;
const isRef = value => isId(value) || (isRecord(value) && isId(value._id));
const optionalText = value => value == null || typeof value === 'string';
const optionalDate = value => value == null || typeof value === 'string' && Number.isFinite(Date.parse(value));
const optionalRef = value => value == null || isRef(value);
const isAddress = value => value == null || isRecord(value)
  && ['streetAddress', 'suburb', 'city', 'state', 'postalCode'].every(field => optionalText(value[field]));
const isGroupDetails = value => value == null || isRecord(value)
  && ['day', 'time', 'frequency'].every(field => optionalText(value[field]));
const isNamedRef = value => optionalRef(value) && (!isRecord(value) || optionalText(value.name));
const isCurriculumRef = value => optionalRef(value) && (!isRecord(value)
  || optionalText(value.title) && (value.metadata == null || isRecord(value.metadata) && optionalText(value.metadata.grade))
    && (value.structure == null || isCurriculumStructure(value.structure)));
const isNestedEntity = value => isRecord(value) && ['User', 'Member', 'Guest'].includes(value.type)
  && (isPersonRef(value.refId) || value.refId === undefined && isPersonRef(value))
  && (value.details == null || isPersonRef(value.details))
  && (value.certifications == null || isCertificationProjection(value.certifications));
const isNestedEntityList = value => Array.isArray(value) && value.every(isNestedEntity);
const isHouseholdRef = value => optionalRef(value) && (!isRecord(value)
  || ['displayName', 'address', 'placeId'].every(field => optionalText(value[field]))
    && isRelationshipPoint(value.location)
    && ['isActive', 'acceptVenueRequest', 'allowRepeatResidenceRequests'].every(field => value[field] === undefined || typeof value[field] === 'boolean')
    && ['members', 'lineages'].every(field => value[field] === undefined || Array.isArray(value[field]) && value[field].every(isPersonRef))
    && optionalRef(value.community) && (value.primaryContact == null || isPersonRef(value.primaryContact))
    && (value.venueApprover == null || isPersonRef(value.venueApprover)));
const isCompletionDetails = value => value == null || isRecord(value)
  && typeof value.isStudyCircle === 'boolean' && optionalText(value.badgeName)
  && Number.isInteger(value.awardedCount) && value.awardedCount >= 0
  && Array.isArray(value.awardedUserIds) && value.awardedUserIds.every(isId)
  && optionalText(value.message);
const isSession = value => isId(value) || (isRecord(value) && isId(value._id) && isSessionTimingProjection(value)
  && isId(value.date) && !Number.isNaN(Date.parse(value.date))
  && ['Scheduled', 'Pending', 'Confirmed', 'Completed', 'Cancelled', 'Expired'].includes(value.status)
  && (value.participants === undefined || isNestedEntityList(value.participants))
  && (value.facilitators === undefined || isNestedEntityList(value.facilitators))
  && (value.pendingParticipants === undefined || isNestedEntityList(value.pendingParticipants))
  && (value.pendingFacilitators === undefined || isNestedEntityList(value.pendingFacilitators))
  && (value.venues === undefined || (Array.isArray(value.venues) && value.venues.every(isVenue)))
  && (value.venueRequests === undefined || (Array.isArray(value.venueRequests) && value.venueRequests.every(isVenueRequest)))
  && (value.venueRequest === undefined || value.venueRequest === null || typeof value.venueRequest === 'boolean' || isRef(value.venueRequest))
  && (value.time == null || typeof value.time === 'string' && value.time.trim().length > 0)
  && isAddress(value.address) && optionalText(value.onlineLink) && optionalDate(value.createdAt)
  && isTypedDetails(value.details)
  && isCurriculumLesson(value.curriculumLesson)
  && (value.curriculumPointer == null || isCurriculumPointer(value.curriculumPointer)));

/** Public and authenticated detail both retain these core fields. */
export function readActivityRecordResponse(payload, operation = 'activity detail') {
  if (!isRecord(payload) || !isId(payload._id) || !isId(payload.title)
    || !Array.isArray(payload.sessions) || !payload.sessions.every(isSession)
    || (payload.sessionsPartial !== undefined && typeof payload.sessionsPartial !== 'boolean')) {
    throw new ActivityResponseContractError(operation, 'expected an activity with _id, title and sessions[].');
  }
  readOccurrenceReadContext(payload);
  if (!isActivityScheduleProjection(payload) || !isNamedRef(payload.activityType) || !isNamedRef(payload.community)
    || (payload.createdBy != null && !isPersonRef(payload.createdBy))
    || !isHouseholdRef(payload.hostHousehold) || !isCurriculumRef(payload.curriculum)
    || payload.curriculumProgress != null && !isCurriculumProgress(payload.curriculumProgress)
    || !isAddress(payload.address) || !isGroupDetails(payload.groupDetails)
    || !['description', 'imageUrl', 'grade'].every(field => optionalText(payload[field]))
    || (payload.status !== undefined && !['Active', 'Complete', 'Cancelled'].includes(payload.status))
    || ['participantLimit', 'facilitatorLimit'].some(field => payload[field] != null
      && (!Number.isInteger(payload[field]) || payload[field] < 0))
    || (payload.isOpenExternally !== undefined && typeof payload.isOpenExternally !== 'boolean')
    || (payload.approvedCommunityVenues !== undefined
      && (!Array.isArray(payload.approvedCommunityVenues) || !payload.approvedCommunityVenues.every(isVenue)))
    || !isCompletionDetails(payload.completionDetails)
    || !isTypedDetails(payload.details, 'activity')) {
    throw new ActivityResponseContractError(operation, 'expected valid activity summaries, relationships and typed details.');
  }
  return payload;
}

/** Collection and user-history endpoints return arrays directly. */
export function readActivityListResponse(payload, operation = 'activity list') {
  if (!Array.isArray(payload)) {
    throw new ActivityResponseContractError(operation, 'expected Activity[].');
  }
  payload.forEach(activity => readActivityRecordResponse(activity, `${operation} item`));
  return payload;
}

/** Creation creates both an activity and its initial session in one response. */
export function readActivityCreationResponse(payload) {
  if (!isRecord(payload) || !isRecord(payload.activity)
    || !isId(payload.activity._id) || !isId(payload.activity.title)
    || !isRecord(payload.session) || !isId(payload.session._id)) {
    throw new ActivityResponseContractError('activity creation', 'expected { activity: {_id, title}, session: {_id} }.');
  }
  readActivityRecordResponse(payload.activity, 'created activity');
  readSessionRecordResponse(payload.session, 'initial activity session');
  if (payload.session.activity !== payload.activity._id || !payload.activity.sessions.some(session => (session?._id || session) === payload.session._id)) {
    throw new ActivityResponseContractError('activity creation', 'initial session must belong to the created activity.');
  }
  return payload;
}

/** Confirmation targets the next session and returns both resources. */
export function readActivityConfirmationResponse(payload) {
  if (!isRecord(payload) || payload.success !== true
    || !['confirmed', 'already_confirmed'].includes(payload.outcome)
    || payload.resourceType !== 'session' || payload.requestedResourceType !== 'activity'
    || !isId(payload.activityId) || !isRecord(payload.activity)
    || payload.activity._id !== payload.activityId
    || !isRecord(payload.session) || !isId(payload.session._id)) {
    throw new ActivityResponseContractError('activity confirmation', 'expected a confirmation outcome with matching activityId, activity and session.');
  }
  readActivityRecordResponse(payload.activity, 'confirmed activity');
  readSessionRecordResponse(payload.session, 'confirmed activity session');
  if (payload.session.activity !== payload.activityId) {
    throw new ActivityResponseContractError('activity confirmation', 'confirmed session must belong to the activity.');
  }
  return payload;
}

/** Delete acknowledges a completed removal with a message. */
export function readActivityDeletionResponse(payload) {
  if (!isRecord(payload) || !isId(payload.message)) {
    throw new ActivityResponseContractError('activity deletion', 'expected { message: string }.');
  }
  return payload;
}

/** Both activity venue helpers return direct venue records marked requestable. */
export function readActivityVenuesResponse(payload) {
  if (!Array.isArray(payload)) {
    throw new ActivityResponseContractError('activity venues', 'expected Venue[].');
  }
  for (const venue of payload) {
    readVenueDetailResponse(venue, 'activity venue');
    if (venue.openForRequest !== true) {
      throw new ActivityResponseContractError('activity venues', 'every venue must be explicitly openForRequest.');
    }
  }
  return payload;
}
